import {
  aiContextDescriptions,
  aiContextFieldConfig,
  aiContexts,
  personalities,
  selectableModelIds,
} from '@tunasong/ai-lib'
import type { AIConfig, AIContext, AIContextSettings, AIFieldMeta } from '@tunasong/ai-lib'
import { Chip, Select, Switch, TextField, Typography } from '@tunasong/ui-lib'
import type { ReactNode, FC, CSSProperties } from 'react'
import { useEffect, useMemo, useState } from 'react'
import { withContextSettings } from './config-updates.js'

const modelOptions = selectableModelIds

/** Extract value from an input change event without depending on DOM types */
const getInputValue = (e: unknown) => (e as { target: { value: string } }).target.value

/**
 * Text field with local state for fast typing.
 * Only propagates to parent on blur to avoid re-rendering the entire form on every keystroke.
 */
const LocalTextField: FC<{
  meta: AIFieldMeta
  value: string | undefined
  globalValue: string | undefined
  showGlobalHint: boolean
  onChange: (value: string | undefined) => void
}> = ({ meta, value, globalValue, showGlobalHint, onChange }) => {
  const [localValue, setLocalValue] = useState(value ?? '')

  // Sync local state when the external value changes (e.g., collaborative edits, config refresh)
  useEffect(() => {
    setLocalValue(value ?? '')
  }, [value])

  return (
    <>
      <TextField
        label={meta.label}
        placeholder={meta.placeholder}
        value={localValue}
        onChange={e => {
          const v = getInputValue(e)
          setLocalValue(v)
          onChange(v || undefined)
        }}
        style={{ width: '100%' }}
      />
      {meta.description ? (
        <Typography variant="caption" style={{ color: 'var(--tuna-palette-text-secondary)', marginTop: 4 }}>
          {meta.description}
        </Typography>
      ) : null}
      {!localValue && globalValue && showGlobalHint && (
        <Chip
          label={`Using global: "${globalValue.length > 40 ? globalValue.slice(0, 40) + '...' : globalValue}"`}
          size="small"
          variant="outlined"
          style={{ marginTop: 4, fontSize: '0.7rem' }}
        />
      )}
    </>
  )
}

interface AIConfigProps {
  defaultValue?: AIConfig
  onChange?: (config: AIConfig) => void
  /** Optional: render a preview panel below the form fields */
  renderPreview?: (props: { context: AIContext; settings: AIContextSettings }) => ReactNode
}

export const AIConfigForm: FC<AIConfigProps> = props => {
  const { defaultValue, onChange, renderPreview } = props

  const [tab, setTab] = useState<AIContext>('global')
  const defaultContexts: Record<AIContext, AIContextSettings> = {
    global: {},
    'song:section': {},
    'song:line': {},
    'song:line-suggestions': {},
    'song:image': {},
    'song:music': {},
    'song:melody': {},
    advisor: {},
  }
  const [config, setConfig] = useState<AIConfig>({
    ...defaultValue,
    contexts: { ...defaultContexts, ...defaultValue?.contexts },
  })

  const contexts = config.contexts
  const selectedContext = useMemo(() => contexts[tab] ?? {}, [contexts, tab])
  const globalContext = useMemo(() => contexts.global ?? {}, [contexts])
  const fieldConfig = aiContextFieldConfig[tab]
  const contextDesc = aiContextDescriptions[tab]

  const updateField = (field: keyof AIContextSettings, value: string | undefined) => {
    const updated = { ...selectedContext, [field]: value || undefined }
    const newConfig = withContextSettings(config, tab, updated)
    setConfig(newConfig)
    onChange?.(newConfig)
  }

  const updateAllowGeneratedMedia = (allowGeneratedMedia: boolean) => {
    const newConfig = { ...config, allowGeneratedMedia }
    setConfig(newConfig)
    onChange?.(newConfig)
  }

  const renderField = (field: keyof AIContextSettings) => {
    const meta = fieldConfig[field]
    if (!meta) {
      return null
    }

    const value = selectedContext[field] as string | undefined
    const globalValue = tab !== 'global' ? (globalContext[field] as string | undefined) : undefined

    if (field === 'model') {
      return (
        <div key={field} style={{ marginBottom: 24, width: '100%' }}>
          <Select
            label={meta.label}
            value={value ?? ''}
            onChange={e => updateField(field, getInputValue(e) || undefined)}
            fullWidth
          >
            <option value="">{globalValue ? `Default (${globalValue})` : 'Default'}</option>
            {modelOptions.map(m => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </Select>
          <Typography variant="caption" style={{ color: 'var(--tuna-palette-text-secondary)', marginTop: 4 }}>
            {meta.description}
          </Typography>
        </div>
      )
    }

    if (field === 'personality') {
      return (
        <div key={field} style={{ marginBottom: 24, width: '100%' }}>
          <Select
            label={meta.label}
            value={value ?? ''}
            onChange={e => updateField(field, getInputValue(e) || undefined)}
            fullWidth
          >
            <option value="">{globalValue ? `Default (${globalValue})` : 'None'}</option>
            {personalities.map(p => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </Select>
          <Typography variant="caption" style={{ color: 'var(--tuna-palette-text-secondary)', marginTop: 4 }}>
            {meta.description}
          </Typography>
        </div>
      )
    }

    // Multiline text fields — uses local state for fast typing, propagates on blur
    return (
      <div key={`${tab}-${field}`} style={{ marginBottom: 24 }}>
        <LocalTextField
          meta={meta}
          value={value}
          globalValue={globalValue}
          showGlobalHint={tab !== 'global'}
          onChange={v => updateField(field, v)}
        />
      </div>
    )
  }

  const visibleFields = Object.keys(fieldConfig) as (keyof AIContextSettings)[]

  const tabStyle: CSSProperties = {
    padding: '8px 16px',
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    textAlign: 'left',
    fontSize: 14,
    color: 'inherit',
    borderLeft: '2px solid transparent',
  }

  const activeTabStyle: CSSProperties = {
    ...tabStyle,
    borderLeftColor: 'var(--tuna-palette-primary-main)',
    color: 'var(--tuna-palette-primary-main)',
    fontWeight: 500,
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'row', height: '100%' }}>
      <div
        style={{
          minWidth: 140,
          display: 'flex',
          flexDirection: 'column',
          borderRight: '1px solid var(--tuna-palette-divider)',
        }}
      >
        {aiContexts.map(context => (
          <button key={context} style={tab === context ? activeTabStyle : tabStyle} onClick={() => setTab(context)}>
            {aiContextDescriptions[context].label}
          </button>
        ))}
      </div>

      <div style={{ flex: 1, marginLeft: 24, overflow: 'auto' }}>
        <Typography
          variant="subtitle1"
          style={{ fontWeight: 'var(--tuna-typography-fontWeight-bold)', marginBottom: 4 }}
        >
          {contextDesc.label}
        </Typography>
        <Typography variant="body2" style={{ color: 'var(--tuna-palette-text-secondary)', marginBottom: 24 }}>
          {contextDesc.description}
        </Typography>

        {visibleFields.map(field => renderField(field))}

        {/*
          Space-wide policy rather than a per-context setting, so it lives on the
          Global tab. Absent means allowed, which is why the Switch reads
          `!== false` rather than defaulting the value.
        */}
        {tab === 'global' ? (
          <div style={{ marginTop: 32, paddingTop: 24, borderTop: '1px solid var(--tuna-palette-divider)' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <Switch
                checked={config.allowGeneratedMedia !== false}
                onCheckedChange={updateAllowGeneratedMedia}
                aria-label="Allow generated images and music"
              />
              <Typography variant="body2">Allow generated images and music</Typography>
            </label>
            <Typography
              variant="caption"
              style={{ color: 'var(--tuna-palette-text-secondary)', display: 'block', marginTop: 4 }}
            >
              Turn this off and nothing here will generate images or music. Suggestions, analysis and explanations keep
              working — this only stops machine-made media being created.
            </Typography>
          </div>
        ) : null}

        {renderPreview?.({ context: tab, settings: selectedContext })}
      </div>
    </div>
  )
}

export default AIConfigForm
